import React, { useState, useEffect, useMemo } from "react";
import { BookmarkIcon, ArrowUpTrayIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { updateBookHoldStatus } from "../../../api/productApi";

function FieldSelect({ label, value, onChange, options, loading = false }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-gray-700 flex items-center gap-2">
        {label}
        {loading && <span className="text-[10px] text-gray-450 animate-pulse">Loading...</span>}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white w-full outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-300 transition-colors shadow-sm text-gray-800"
      >
        {options.map((o, i) => (
          <option key={i} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function BookHoldSection({ book, token, copyOptions, copiesLoading, showToast }) {
  const [holdReason, setHoldReason] = useState("");
  const [holdStatus, setHoldStatus] = useState("put_on_hold");
  const [holdCopyId, setHoldCopyId] = useState("");
  const [holdRemark, setHoldRemark] = useState("");
  const [holdSendEmail, setHoldSendEmail] = useState(false);
  const [holdImages, setHoldImages] = useState([]);
  const [holdHistory, setHoldHistory] = useState([]);
  const [holdSubmitting, setHoldSubmitting] = useState(false);
  const [holdMsg, setHoldMsg] = useState("");
  const [holdErr, setHoldErr] = useState("");

  const holdReasonOptions = useMemo(() => [
    { label: "Select Dropdown", value: "" },
    { label: "Damage repair", value: "damage_repair" },
    { label: "Back from repair", value: "back_from_repair" },
    { label: "Customer request", value: "customer_request" },
    { label: "Other", value: "other" },
  ], []);

  const holdStatusOptions = useMemo(() => [
    { label: "Put on Hold", value: "put_on_hold" },
    { label: "Released", value: "released" },
  ], []);

  /* ------------------ LOAD HOLD HISTORY ------------------ */
  useEffect(() => {
    if (book?.hold_history) {
      setHoldHistory(book.hold_history);
    }
  }, [book]);

  /* ------------------ AUTOFILL HOLD HISTORY ------------------ */
  useEffect(() => {
    if (!holdCopyId) {
      setHoldStatus("put_on_hold");
      setHoldReason("");
      setHoldRemark("");
      setHoldSendEmail(false);
      setHoldImages([]);
      return;
    }

    const matchingRecords = holdHistory.filter(
      (h) => String(h.copy_post_id) === String(holdCopyId)
    );

    if (matchingRecords.length > 0) {
      const latest = [...matchingRecords].sort((a, b) => {
        const dateA = a.date ? new Date(a.date) : new Date(0);
        const dateB = b.date ? new Date(b.date) : new Date(0);
        return dateB - dateA;
      })[0];

      setHoldStatus(latest.hold_status || "put_on_hold");
      setHoldReason(latest.reason || "");
      setHoldRemark(latest.remark || "");
      setHoldSendEmail(
        latest.email_notification === "yes" ||
        latest.email_notification === "1" ||
        latest.email_notification === 1 ||
        latest.email_notification === true
      );
      if (Array.isArray(latest.images)) {
        setHoldImages(
          latest.images.map((imgUrl, index) => ({
            id: `existing-${index}-${imgUrl}`,
            url: imgUrl,
            isExisting: true,
          }))
        );
      } else {
        setHoldImages([]);
      }
    } else {
      setHoldStatus("put_on_hold");
      setHoldReason("");
      setHoldRemark("");
      setHoldSendEmail(false);
      setHoldImages([]);
    }
  }, [holdCopyId, holdHistory]);

  /* ------------------ BOOK ON HOLD HANDLERS ------------------ */
  async function handleHoldImageUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    try {
      const newHoldImages = await Promise.all(
        files.map(async (file, idx) => {
          const base64 = await toBase64(file);
          return {
            id: `new-${Date.now()}-${idx}-${Math.random()}`,
            base64: base64,
            url: URL.createObjectURL(file),
            isExisting: false,
          };
        })
      );
      setHoldImages((prev) => [...prev, ...newHoldImages]);
    } catch (err) {
      console.error("Hold Image convert failed", err);
      showToast("Failed to upload image(s)", "error");
    }
  }

  const handleRemoveHoldImage = (id) => {
    setHoldImages((prev) => prev.filter((img) => img.id !== id));
  };

  async function handleSaveHold() {
    setHoldMsg("");
    setHoldErr("");

    if (!book?.id) {
      const errMessage = "Product ID is required.";
      setHoldErr(errMessage);
      showToast(errMessage, "error");
      return;
    }
    if (!holdCopyId) {
      const errMessage = "Copy ID is required.";
      setHoldErr(errMessage);
      showToast(errMessage, "error");
      return;
    }
    if (!holdStatus) {
      const errMessage = "Hold status is required.";
      setHoldErr(errMessage);
      showToast(errMessage, "error");
      return;
    }
    if (!holdReason) {
      const errMessage = "Reason is required.";
      setHoldErr(errMessage);
      showToast(errMessage, "error");
      return;
    }

    const payload = {
      sq_product_id: Number(book.id),
      sq_copy_id: Number(holdCopyId),
      hold_status: holdStatus,
      reason: holdReason,
      remark: holdRemark || "",
      email_notification: holdSendEmail,
      images: holdImages
        .filter((img) => !img.isExisting)
        .map((img) => img.base64),
    };

    try {
      setHoldSubmitting(true);
      const res = await updateBookHoldStatus({ token, payload });

      if (res?.success) {
        setHoldMsg(res.message || "Hold status updated successfully!");
        showToast("Hold status updated!");

        if (res.hold_history) {
          setHoldHistory(res.hold_history);
        } else if (res.data?.hold_history) {
          setHoldHistory(res.data.hold_history);
        }
      } else {
        setHoldErr(res?.message || "Failed to update hold status");
        showToast(res?.message || "Failed to update hold status", "error");
      }
    } catch (error) {
      setHoldErr(error.message || "Server error updating hold");
      showToast("Server error updating hold", "error");
    } finally {
      setHoldSubmitting(false);
    }
  }

  return (
    <div className="border border-gray-200 rounded-xl p-6 bg-white shadow-sm">
      <div className="flex items-center gap-2 mb-6">
        <BookmarkIcon className="h-5 w-5 text-gray-900" />
        <h3 className="font-bold text-lg text-gray-900">Book on Hold</h3>
      </div>

      {/* INPUT GRID */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
        <FieldSelect
          label="Reason:"
          value={holdReason}
          onChange={setHoldReason}
          options={holdReasonOptions}
        />
        <FieldSelect
          label="Hold Status:"
          value={holdStatus}
          onChange={setHoldStatus}
          options={holdStatusOptions}
        />
        <FieldSelect
          label="Copy:"
          value={holdCopyId}
          onChange={setHoldCopyId}
          options={copyOptions}
          loading={copiesLoading}
        />
      </div>

      {/* REMARK - FULL WIDTH */}
      <div className="flex flex-col gap-1.5 mb-6">
        <label className="text-xs font-semibold text-gray-700">Remark:</label>
        <input
          type="text"
          value={holdRemark}
          onChange={(e) => setHoldRemark(e.target.value)}
          placeholder="Remarks"
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white w-full outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-300 transition-colors shadow-sm text-gray-800"
        />
      </div>

      {/* IMAGES PREVIEW */}
      {holdImages.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-6">
          {holdImages.map((img) => (
            <div key={img.id} className="relative group w-20 h-20 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 shadow-sm transition-all hover:scale-105">
              <img
                src={img.url}
                alt="Hold Preview"
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => handleRemoveHoldImage(img.id)}
                className="absolute top-1 right-1 bg-black/60 hover:bg-black text-white rounded-full p-1 transition-colors shadow"
                title="Remove Image"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* EMAIL & BUTTONS ROW */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={holdSendEmail}
            onChange={(e) => setHoldSendEmail(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
          />
          Send E-Mail Notification
        </label>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 border border-gray-300 px-4 py-2 rounded-lg cursor-pointer text-sm bg-white hover:bg-gray-50 font-medium transition-colors shadow-sm">
            <ArrowUpTrayIcon className="h-4 w-4 text-gray-700" />
            Upload Images
            <input
              type="file"
              multiple
              hidden
              accept="image/*"
              onChange={handleHoldImageUpload}
            />
          </label>
          {holdImages.length > 0 && (
            <span className="text-xs text-green-600 font-semibold mr-2">
              {holdImages.length} image(s)
            </span>
          )}

          <button
            onClick={handleSaveHold}
            disabled={holdSubmitting}
            className={`px-8 py-2 rounded-lg text-white font-semibold text-sm shadow-sm transition-colors ${holdSubmitting ? "bg-gray-400 cursor-not-allowed" : "bg-black hover:bg-gray-800"}`}
          >
            {holdSubmitting ? "Saving..." : "Save Hold"}
          </button>
        </div>
      </div>

      {/* ERROR / SUCCESS */}
      {holdErr && <p className="text-red-500 text-sm mt-4">⚠️ {holdErr}</p>}
      {holdMsg && <p className="text-green-600 text-sm mt-4">✅ {holdMsg}</p>}
    </div>
  );
}
